/**
 * Auth screen — split-screen brand panel on the left, login/signup form on
 * the right. Below the responsive breakpoint the brand panel collapses to a
 * compact header.
 */
import { useRef, useState } from "react";
import * as api from "../api";
import { authStore, prefsStore } from "../store";
import { Btn, Field, IconBtn, useLanguages } from "../ui";
import { Icon } from "../icons";
import { Wordmark } from "../logo";
import { LegalModal } from "../legal";

const CONTACT_EMAIL = "info@aevum-intel.com";

const linkBtnStyle: React.CSSProperties = {
  background: "none", border: "none", padding: 0, cursor: "pointer",
  font: "inherit", color: "var(--accent)", fontWeight: 600,
};

export function AuthScreen({
  theme, toggleTheme, tiny, onAuthed,
}: {
  theme: "light" | "dark"; toggleTheme: () => void; tiny: boolean;
  onAuthed: (hasActiveEnrollment: boolean) => void;
}) {
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [ownerLang, setOwnerLang] = useState("en-US");
  const [otherLang, setOtherLang] = useState("hi-IN");
  const [gender, setGender] = useState<"female" | "male">("female");
  const [busy, setBusy] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (mode === "signup" && !agreed) {
      setErr("Please accept the Terms of Service & Privacy Notice to continue.");
      return;
    }
    setBusy(true);
    try {
      const deviceId = prefsStore.getState().deviceId;
      if (mode === "login") {
        const tok = await api.login({ email, password, device_id: deviceId, device_label: "web" });
        authStore.getState().setTokens(tok);
        const me = await api.getMe();
        authStore.getState().setUser(me);
        const enrollments = await api.listEnrollments().catch(() => []);
        onAuthed(enrollments.some((x) => x.active));
      } else {
        const tok = await api.signup({
          email, password, display_name: displayName || email.split("@")[0]!,
          owner_lang: ownerLang, fallback_other_lang: otherLang,
          tts_voice_gender: gender, device_id: deviceId, device_label: "web",
        });
        authStore.getState().setTokens(tok);
        const me = await api.getMe();
        authStore.getState().setUser(me);
        onAuthed(false);
      }
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : String(e2));
    } finally {
      setBusy(false);
    }
  }

  async function startDemo() {
    if (demoBusy) return;
    setErr(null);
    setDemoBusy(true);
    try {
      const deviceId = prefsStore.getState().deviceId;
      const creds = generateDemoCredentials();
      const tok = await api.signup({
        email: creds.email,
        password: creds.password,
        display_name: "Demo",
        owner_lang: "en-US",
        fallback_other_lang: "hi-IN",
        tts_voice_gender: "female",
        device_id: deviceId,
        device_label: "web-demo",
      });
      // Mark this as a demo session (in-memory only). App.tsx swaps to
      // DemoScreen because `isDemo` flips to true.
      authStore.getState().setDemoTokens(tok);
      try {
        const me = await api.getMe();
        authStore.getState().setUser(me);
      } catch { /* DemoScreen will retry */ }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setDemoBusy(false);
    }
  }

  return (
    <div style={{ display: "flex", minHeight: "100vh", width: "100vw" }}>
      {!tiny && (
        <div style={{
          width: "46%", maxWidth: 620,
          background: "linear-gradient(135deg, var(--you) 0%, var(--accent) 60%, var(--accent-press) 100%)",
          color: "var(--on-accent)", padding: "44px 48px",
          display: "flex", flexDirection: "column", justifyContent: "space-between",
          position: "relative", overflow: "hidden",
        }}>
          <Wordmark size={32} caption ink="var(--on-accent)" wave="var(--on-accent)" />
          <div style={{ position: "relative", zIndex: 1 }}>
            <div className="display" style={{ fontSize: 42, fontWeight: 700, lineHeight: 1.05, letterSpacing: -1, marginBottom: 16 }}>
              Talk freely.<br />Be understood,<br />everywhere.
            </div>
            <p style={{ fontSize: 16.5, lineHeight: 1.5, opacity: 0.88, maxWidth: 440 }}>
              Real-time, two-speaker translation that adapts to your voice — so
              every conversation flows naturally.
            </p>
            <div style={{
              marginTop: 26, borderRadius: 18, overflow: "hidden",
              boxShadow: "0 18px 50px rgba(0,0,0,0.28)", background: "#000",
              border: "1px solid rgba(255,255,255,0.16)",
            }}>
              <DemoVideo />
            </div>
          </div>
        </div>
      )}
      <div style={{
        flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", padding: "32px 24px", position: "relative",
      }}>
        <div style={{ position: "absolute", top: 18, right: 18 }}>
          <IconBtn name={theme === "dark" ? "sun" : "moon"} onClick={toggleTheme} variant="surface" size={38} ic={18} />
        </div>
        {tiny && <div style={{ marginBottom: 24 }}><Wordmark size={26} /></div>}
        {tiny && (
          <div style={{
            width: "100%", maxWidth: 380, marginBottom: 20, borderRadius: 16, overflow: "hidden",
            background: "#000", boxShadow: "var(--shadow-md)", border: "1px solid var(--border)",
          }}>
            <DemoVideo />
          </div>
        )}
        <form onSubmit={submit} style={{ width: "100%", maxWidth: 380, display: "flex", flexDirection: "column", gap: 14 }}>
          <Btn type="button" variant="primary" size="lg" full icon="sparkle"
            onClick={startDemo} disabled={demoBusy || busy}>
            {demoBusy ? "Spinning up demo…" : "Try demo"}
          </Btn>
          <div style={{
            fontSize: 12, color: "var(--ink-3)", textAlign: "center", marginTop: -4, lineHeight: 1.45,
          }}>
            Talk to an <strong style={{ color: "var(--ink-2)" }}>AI stand-in</strong> for the second
            speaker. In the real product the other side is a real person —
            we just fake them here so you can try it solo.
          </div>

          <div style={{
            display: "flex", alignItems: "center", gap: 10, margin: "4px 0",
            color: "var(--ink-faint)", fontSize: 12, fontFamily: "var(--font-ui)",
          }}>
            <span style={{ flex: 1, height: 1, background: "var(--border)" }} />
            <span>or</span>
            <span style={{ flex: 1, height: 1, background: "var(--border)" }} />
          </div>

          <div className="display" style={{ fontSize: 28, fontWeight: 700, color: "var(--ink)", letterSpacing: -0.5 }}>
            {mode === "login" ? "Welcome back" : "Create your account"}
          </div>
          <div style={{ display: "flex", background: "var(--surface-2)", borderRadius: "var(--r-pill)", padding: 4 }}>
            {(["login", "signup"] as const).map((m) => (
              <button key={m} type="button" onClick={() => setMode(m)} style={{
                flex: 1, height: 38, borderRadius: "var(--r-pill)", border: "none", cursor: "pointer",
                fontSize: 14.5, fontWeight: 600, fontFamily: "var(--font-ui)",
                background: mode === m ? "var(--surface)" : "transparent",
                color: mode === m ? "var(--ink)" : "var(--ink-3)",
                boxShadow: mode === m ? "var(--shadow-sm)" : "none",
              }}>{m === "login" ? "Sign in" : "Sign up"}</button>
            ))}
          </div>
          {mode === "signup" && (
            <Field label="Display name" icon="user" value={displayName} onChange={setDisplayName} placeholder="Alex Diaz" />
          )}
          <Field label="Email" icon="mail" value={email} onChange={setEmail} type="email" placeholder="you@example.com" />
          <Field label="Password" icon="lock" value={password} onChange={setPassword} type={showPw ? "text" : "password"}
            placeholder={mode === "signup" ? "At least 12 characters" : "Your password"}
            right={<button type="button" onClick={() => setShowPw((s) => !s)} style={{
              background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-3)", display: "flex", padding: 4,
            }}><Icon name="eye" size={18} /></button>} />
          {mode === "signup" && (
            <>
              <LangPickerInline label="I speak" code={ownerLang} setCode={setOwnerLang} />
              <LangPickerInline label="Translate to" code={otherLang} setCode={setOtherLang} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-2)", marginBottom: 7 }}>Spoken voice</div>
                <div style={{ display: "flex", gap: 8 }}>
                  {(["female", "male"] as const).map((g) => (
                    <button key={g} type="button" onClick={() => setGender(g)} style={{
                      flex: 1, height: 44, borderRadius: 14, cursor: "pointer", fontSize: 14, fontWeight: 600,
                      fontFamily: "var(--font-ui)", textTransform: "capitalize",
                      border: `1.5px solid ${gender === g ? "var(--accent)" : "var(--border)"}`,
                      background: gender === g ? "var(--accent-soft)" : "var(--surface)",
                      color: gender === g ? "var(--accent-ink)" : "var(--ink-2)",
                    }}>{g}</button>
                  ))}
                </div>
              </div>
            </>
          )}
          {mode === "signup" && (
            <label style={{
              display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer",
              fontSize: 13, color: "var(--ink-2)", lineHeight: 1.45,
            }}>
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={{
                marginTop: 2, width: 16, height: 16, accentColor: "var(--accent)", cursor: "pointer", flexShrink: 0,
              }} />
              <span>
                I agree to the{" "}
                <button type="button" onClick={() => setShowTerms(true)} style={linkBtnStyle}>
                  Terms of Service &amp; Privacy Notice
                </button>.
              </span>
            </label>
          )}
          {err && <div style={{
            background: "color-mix(in oklch, var(--bad) 12%, transparent)",
            color: "var(--bad)", padding: "9px 12px", borderRadius: 12, fontSize: 13.5,
          }}>{err}</div>}
          <Btn type="submit" size="lg" full disabled={busy || (mode === "signup" && !agreed)}>
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </Btn>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
            marginTop: 2, fontSize: 12.5, color: "var(--ink-3)", fontFamily: "var(--font-ui)",
          }}>
            <button type="button" onClick={() => setShowTerms(true)} style={linkBtnStyle}>Terms &amp; Privacy</button>
            <span aria-hidden style={{ opacity: 0.6 }}>·</span>
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ ...linkBtnStyle, textDecoration: "none" }}>Contact us</a>
          </div>
        </form>
        <LegalModal open={showTerms} onClose={() => setShowTerms(false)} />
      </div>
    </div>
  );
}

function LangPickerInline({ label, code, setCode }: { label: string; code: string; setCode: (c: string) => void }) {
  const langs = useLanguages();
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-2)", marginBottom: 7 }}>{label}</div>
      <select value={code} onChange={(e) => setCode(e.target.value)} style={{
        width: "100%", height: 50, padding: "0 14px", fontSize: 15.5, color: "var(--ink)",
        background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: "var(--r-md)",
        fontFamily: "var(--font-ui)", appearance: "none", cursor: "pointer",
      }}>
        {langs.map((l) => <option key={l.code} value={l.code}>{l.name} — {l.native}</option>)}
      </select>
    </div>
  );
}

/**
 * Generate single-use credentials for an anonymous demo account. The email
 * uses a non-deliverable `.demo` TLD so it never collides with a real user.
 * The password meets the 12-character minimum the server enforces.
 */
function generateDemoCredentials(): { email: string; password: string } {
  const id = (typeof crypto !== "undefined" && "randomUUID" in crypto)
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);
  const slug = id.replace(/-/g, "").slice(0, 16);
  const pwdBytes = new Uint8Array(18);
  if (typeof crypto !== "undefined" && "getRandomValues" in crypto) {
    crypto.getRandomValues(pwdBytes);
  } else {
    for (let i = 0; i < pwdBytes.length; i++) pwdBytes[i] = Math.floor(Math.random() * 256);
  }
  // Base64url, but keep a guaranteed mix of an upper, lower, digit & symbol
  // so the server's complexity check (if any) passes.
  const b64 = btoa(String.fromCharCode(...pwdBytes))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  return {
    email: `demo-${slug}@aevum.demo`,
    password: `Aa1!${b64}`.slice(0, 32),
  };
}

/**
 * Landing-page demo video. Autoplays muted+looping (required for mobile
 * autoplay). Shows a "Tap for sound" overlay until the user unmutes, which
 * is the only gesture browsers accept to enable audio.
 */
function DemoVideo() {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [muted, setMuted] = useState(true);

  function enableSound() {
    const v = ref.current;
    if (!v) return;
    v.muted = false;
    v.volume = 1;
    setMuted(false);
    void v.play().catch(() => {});
  }

  return (
    <div style={{ position: "relative", display: "block" }}>
      <video
        ref={ref}
        src="/app/aevum_turntaking_demo.mp4"
        autoPlay muted loop playsInline controls
        preload="metadata"
        onVolumeChange={(e) => setMuted(e.currentTarget.muted || e.currentTarget.volume === 0)}
        style={{ display: "block", width: "100%", aspectRatio: "1280 / 800" }}
      />
      {muted && (
        <button
          type="button"
          onClick={enableSound}
          aria-label="Tap for sound"
          style={{
            position: "absolute", inset: 0, zIndex: 2, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            border: "none", background: "rgba(0,0,0,0.18)",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <span style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "9px 16px", borderRadius: 999,
            background: "rgba(0,0,0,0.62)", color: "#fff",
            fontFamily: "var(--font-ui)", fontSize: 13.5, fontWeight: 600,
            backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
            boxShadow: "0 4px 14px rgba(0,0,0,0.3)",
          }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 5 6 9H2v6h4l5 4z" />
              <path d="M15.5 8.5a5 5 0 0 1 0 7" />
              <path d="M19 5a9 9 0 0 1 0 14" />
            </svg>
            Tap for sound
          </span>
        </button>
      )}
    </div>
  );
}

